import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Demo from "@/pages/Demo";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import InviteAccept from "@/pages/InviteAccept";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Presentation from "@/pages/Presentation";
import Admin from "@/pages/Admin";
import Team from "@/pages/Team";
import PaymentResult from "@/pages/PaymentResult";
import History from "@/pages/History";
import Messages from "@/pages/Messages";
import Settings from "@/pages/Settings";
import Store from "@/pages/Store";
import Solutions from "@/pages/Solutions";
import Solution from "@/pages/Solution";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import MasonBrain from "@/pages/MasonBrain";
import Art from "@/pages/Art";
import AdminConsole from "@/pages/AdminConsole";
import { WelcomeTour } from "@/components/WelcomeTour";
import { MobileNav } from "@/components/MobileNav";
import { StudioRail } from "@/components/StudioRail";

const RAIL_PUBLIC = ["/login", "/signup", "/invite", "/forgot-password", "/reset-password", "/demo", "/", "/solutions", "/journal", "/art"];

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center bg-[var(--bone)] text-[var(--ink-soft)]">Loading…</div>
);

function Protected({ children, editors, owner, superadmin }) {
  const { status, user } = useAuth();
  if (status === "loading") return <Loading />;
  if (status === "anon") return <Navigate to="/" replace />;
  if (superadmin && !user.is_superadmin) return <Navigate to="/studio" replace />;
  if (owner && user.role !== "owner") return <Navigate to="/studio" replace />;
  if (editors && !["owner", "manager"].includes(user.role)) return <Navigate to="/studio" replace />;
  return children;
}

function HomeGate() {
  const { status } = useAuth();
  if (status === "loading") return <Loading />;
  if (status === "authed") return <Navigate to="/studio" replace />;
  return <Landing />;
}

function Routed() {
  return (
    <Routes>
      <Route path="/" element={<HomeGate />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/solutions" element={<Solutions />} />
      <Route path="/solutions/:slug" element={<Solution />} />
      <Route path="/journal" element={<Journal />} />
      <Route path="/journal/:slug" element={<JournalPost />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/invite/:code" element={<InviteAccept />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:code" element={<ResetPassword />} />
      <Route path="/studio" element={<Protected><Presentation /></Protected>} />
      <Route path="/history" element={<Protected><History /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/settings" element={<Protected owner><Settings /></Protected>} />
      <Route path="/store" element={<Protected><Store /></Protected>} />
      <Route path="/console" element={<Protected superadmin><AdminConsole /></Protected>} />
      <Route path="/brain" element={<Protected superadmin><MasonBrain /></Protected>} />
      <Route path="/library" element={<Protected editors><Admin /></Protected>} />
      <Route path="/art" element={<Protected editors><Art /></Protected>} />
      <Route path="/team" element={<Protected editors><Team /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentResult cancel /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppFrame() {
  const { status } = useAuth();
  const { pathname } = useLocation();
  const onPublic = RAIL_PUBLIC.some((r) => (r === "/" ? pathname === "/" : pathname.startsWith(r)));
  const railed = status === "authed" && !onPublic;
  return (
    <>
      <div className={railed ? "lg:pr-[84px]" : ""}>
        <Routed />
      </div>
      <WelcomeTour />
      <MobileNav />
      <StudioRail />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppFrame />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
